import {
  type ComputedRef,
  computed,
  proxyRefs,
  type ShallowRef,
  shallowRef,
} from 'vue';
import { currentLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { Payment, payment } from '@/services/Payment';
import { printing } from '@/services/Printing';
import { isPaymentAccepted } from '@/services/print-copy-payment';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import type { PaymentFailure, PaymentIntentResponse } from '@/shared/types/api';
import { silently, track } from '@/shared/utils/track-telemetry';
import {
  finalPaymentCaptionKey,
  PAYMENT_FAILURE_CAPTION_KEYS,
  type PaymentFailureCaptionKey,
  paymentErrorCaptionKey,
} from './paymentFailureCaption';
import {
  PAYMENT_TERMINAL_STATES,
  type PaymentTerminalError,
  type PaymentTerminalPublicApi,
  type PaymentTerminalState,
} from './paymentTerminalTypes';
import { FINAL_SCREEN_AUTO_END_MS } from './useSessionEndScreen';

// The failed payments of one visit since its last payment that worked
// (Payment.failedPaymentCount). The failure with this number is final: the
// screen has no retry, only the exit. A cancel on the reader is not a failure.
export const MAX_PAYMENT_ATTEMPTS = 3;

type PublicApi = PaymentTerminalPublicApi<typeof store, typeof router>;

type PaymentErrorExitSource = 'error_screen' | 'final_screen_timeout';

interface PaymentRejection {
  code?: string;
  failure?: PaymentFailure | null;
  message?: unknown;
}

export interface PaymentTerminalFlow {
  enter: (exposePublicApi: () => void) => void;
  errorCaptionKey: ShallowRef<PaymentFailureCaptionKey>;
  getTotalSumFormatted: () => string;
  isFinalPaymentError: ComputedRef<boolean>;
  isNavigating: ShallowRef<boolean>;
  isPaymentError: ComputedRef<boolean>;
  isPaymentSuccessful: ComputedRef<boolean>;
  isUnmounted: ShallowRef<boolean>;
  isWaitingForPayment: ComputedRef<boolean>;
  launch: () => void;
  leave: () => void;
  onCancelClick: () => void;
  onExitSessionClick: () => Promise<void>;
  onPopupContinueClick: () => void;
  onPopupExitClick: () => Promise<void>;
  onRetryClick: () => void;
  paymentAttemptCount: ShallowRef<number>;
  paymentStartTime: ShallowRef<number | null>;
  publicApi: PublicApi;
  showTerminalCancelledPopup: ShallowRef<boolean>;
  state: ShallowRef<PaymentTerminalState>;
}

function errorMessage(error: unknown): unknown {
  if (error && typeof error === 'object' && 'message' in error) {
    return error.message;
  }
  return undefined;
}

function warn(...args: unknown[]): void {
  // biome-ignore lint/suspicious/noConsole: preserve payment support diagnostics.
  console.warn(...args);
}

function reportError(...args: unknown[]): void {
  // biome-ignore lint/suspicious/noConsole: preserve payment support diagnostics.
  console.error(...args);
}

export function usePaymentTerminalFlow(): PaymentTerminalFlow {
  const state = shallowRef<PaymentTerminalState>(PAYMENT_TERMINAL_STATES.IDLE);
  const showTerminalCancelledPopup = shallowRef(false);
  const isUnmounted = shallowRef(false);
  const isNavigating = shallowRef(false);
  const paymentStartTime = shallowRef<number | null>(null);
  const paymentAttemptCount = shallowRef(0);
  const failedPaymentCount = shallowRef(payment.failedPaymentCount);
  const errorCaptionKey = shallowRef<PaymentFailureCaptionKey>(
    PAYMENT_FAILURE_CAPTION_KEYS.generic
  );
  let finalAutoExitTimeout: ReturnType<typeof setTimeout> | null = null;

  const isWaitingForPayment = computed(
    () => state.value === PAYMENT_TERMINAL_STATES.WAITING
  );
  const isPaymentSuccessful = computed(
    () => state.value === PAYMENT_TERMINAL_STATES.SUCCESS
  );
  const isPaymentError = computed(
    () => state.value === PAYMENT_TERMINAL_STATES.ERROR
  );
  const isFinalPaymentError = computed(
    () =>
      isPaymentError.value && failedPaymentCount.value >= MAX_PAYMENT_ATTEMPTS
  );

  function clearFinalAutoExit(): void {
    if (finalAutoExitTimeout === null) {
      return;
    }
    clearTimeout(finalAutoExitTimeout);
    finalAutoExitTimeout = null;
  }

  function showPaymentError(causeKey: PaymentFailureCaptionKey): void {
    payment.failedPaymentCount += 1;
    failedPaymentCount.value = payment.failedPaymentCount;
    const isFinal = failedPaymentCount.value >= MAX_PAYMENT_ATTEMPTS;
    errorCaptionKey.value = isFinal
      ? finalPaymentCaptionKey(causeKey)
      : causeKey;
    state.value = PAYMENT_TERMINAL_STATES.ERROR;
    if (!isFinal) {
      return;
    }
    clearFinalAutoExit();
    finalAutoExitTimeout = setTimeout(() => {
      finalAutoExitTimeout = null;
      void exitAfterPaymentError('final_screen_timeout');
    }, FINAL_SCREEN_AUTO_END_MS);
  }

  function getTotalSumFormatted(): string {
    const reprintOrder = printing.reprintOrder as {
      amountCents: number;
    } | null;
    return reprintOrder
      ? payment.getTotalSumFormatted(
          store.printingQueue,
          reprintOrder.amountCents
        )
      : payment.getTotalSumFormatted(store.printingQueue);
  }

  // The copies that the payment pays, declared before the payment intent
  // exists. The amounts come from the same queue, the same total and the same
  // allocation as bindPaymentToCopies, thus each copy later sends the amount
  // that the backend expects. Null: the kiosk captures at the payment.
  function declaredCopyAmounts(): number[] | null {
    try {
      if (printing.reprintOrder) {
        return printing.reprintCopyAmounts();
      }
      return printing.orderCopyAmounts(
        payment.getCopyAmountsInCents(
          store.printingQueue,
          payment.getTotalSumInCents(store.printingQueue)
        )
      );
    } catch (error) {
      warn('[PaymentTerminal] Copy declaration failed:', errorMessage(error));
      return null;
    }
  }

  // The accepted payment pays for these copies: each copy is sent with its
  // payment and its amount. For a held payment, the backend does not take the
  // amount of a failed copy. For a payment captured at the payment, the
  // backend refunds the amount of a failed copy.
  function bindPaymentToCopies(paymentIntent: PaymentIntentResponse): void {
    if (printing.reprintOrder) {
      printing.bindReprintPayment(paymentIntent.id);
      return;
    }
    printing.bindOrderPayment(
      paymentIntent.id,
      payment.getCopyAmountsInCents(store.printingQueue, paymentIntent.amount)
    );
  }

  // The attempt of an earlier card screen still runs: a cancel ends the
  // screen at once, but the attempt can wait for a slow send to the reader.
  // This screen waits for that end and then starts its own payment.
  function launchAfterEarlierAttempt(): void {
    state.value = PAYMENT_TERMINAL_STATES.WAITING;
    void payment.whenIdle().then(() => {
      if (
        isUnmounted.value ||
        isNavigating.value ||
        payment.isPaymentInProgress
      ) {
        return;
      }
      state.value = PAYMENT_TERMINAL_STATES.IDLE;
      launch();
    });
  }

  function launch(): void {
    if (state.value === PAYMENT_TERMINAL_STATES.WAITING) {
      warn(
        '[PaymentTerminal] launch() called while payment already in progress, ignoring'
      );
      return;
    }

    if (isUnmounted.value) {
      return;
    }

    if (payment.isPaymentInProgress) {
      launchAfterEarlierAttempt();
      return;
    }

    state.value = PAYMENT_TERMINAL_STATES.WAITING;
    showTerminalCancelledPopup.value = false;
    errorCaptionKey.value = PAYMENT_FAILURE_CAPTION_KEYS.generic;
    paymentAttemptCount.value++;
    paymentStartTime.value = performance.now();

    track(
      '[PaymentTerminal] Sentry tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'payment',
          'Payment attempt started',
          {
            attempt_number: paymentAttemptCount.value,
            queue_size: store.printingQueue?.length || 0,
          },
          'info'
        );
      },
      warn
    );

    if (!store.printingQueue || store.printingQueue.length === 0) {
      const error = new Error('Cannot launch payment: printing queue is empty');
      reportError('[PaymentTerminal]', error.message);

      track(
        '[PaymentTerminal] Sentry tracking failed:',
        () => {
          sentryService.captureException(error, {
            tags: {
              payment_error_type: 'empty_queue',
              payment_step: 'validation',
            },
            extra: {
              attempt_number: paymentAttemptCount.value,
            },
            level: 'error',
          });
          sentryService.incrementCounter('payment.validation_failure', 1, {
            reason: 'empty_queue',
          });
        },
        warn
      );

      showPaymentError(PAYMENT_FAILURE_CAPTION_KEYS.generic);
      return;
    }

    const launchAttempt = paymentAttemptCount.value;
    const reprintOrder = printing.reprintOrder as {
      amountCents: number;
    } | null;
    const copyAmountsCents = declaredCopyAmounts();

    (
      (reprintOrder
        ? payment.processPayment(store.printingQueue, {
            amountCents: reprintOrder.amountCents,
            copyAmountsCents,
          })
        : payment.processPayment(store.printingQueue, {
            copyAmountsCents,
          })) as Promise<PaymentIntentResponse>
    )
      .then((paymentIntent) => {
        if (isUnmounted.value) {
          warn(
            '[PaymentTerminal] Payment succeeded but component already unmounted'
          );
          return;
        }

        if (launchAttempt !== paymentAttemptCount.value) {
          warn(
            '[PaymentTerminal] Stale success callback from attempt',
            launchAttempt,
            'current:',
            paymentAttemptCount.value
          );
          return;
        }

        if (isNavigating.value) {
          warn(
            '[PaymentTerminal] Payment succeeded but user already navigating away'
          );
          return;
        }

        // A held payment of a declared order is paid: the backend captures
        // the printed copies after the print.
        if (!isPaymentAccepted(paymentIntent)) {
          const failure = paymentIntent?.failure;
          const error = new Error(
            failure?.message || 'Payment not successful'
          ) as PaymentTerminalError;
          error.code = failure?.code;
          error.declineCode = failure?.decline_code;
          error.category = failure?.category;
          error.failure = failure ?? null;
          error.paymentStatus = paymentIntent?.status;
          if (failure) {
            error.cause = failure;
          }

          silently(() => {
            sentryService.captureException(error, {
              tags: {
                payment_error_type: 'capture_not_succeeded',
                payment_failure_category: failure?.category ?? 'unknown',
              },
              extra: {
                payment_status: paymentIntent?.status,
                payment_outcome: paymentIntent?.outcome,
                failure_code: failure?.code,
                decline_code: failure?.decline_code,
              },
              level: 'warning',
            });
          });
          throw error;
        }

        silently(() => {
          sentryService.setContext('payment', null);
        });

        // A visitor who paid and then buys again (a reprint or a second order)
        // gets three tries again.
        payment.failedPaymentCount = 0;
        failedPaymentCount.value = 0;

        // A binding fault must not stop a paid print: unbound copies keep the
        // full-session refund of the backend.
        silently(() => bindPaymentToCopies(paymentIntent));
        state.value = PAYMENT_TERMINAL_STATES.SUCCESS;
        isNavigating.value = true;
        router.redirectToStep('printing');
      })
      .catch((error: unknown) => {
        if (isUnmounted.value) {
          warn(
            '[PaymentTerminal] Payment failed but component already unmounted'
          );
          return;
        }

        if (launchAttempt !== paymentAttemptCount.value) {
          warn(
            '[PaymentTerminal] Stale error callback from attempt',
            launchAttempt,
            'current:',
            paymentAttemptCount.value
          );
          return;
        }

        if (isNavigating.value) {
          return;
        }

        const paymentError = error as PaymentRejection | null | undefined;
        reportError(
          '[PaymentTerminal] Payment failed:',
          paymentError?.code || 'UNKNOWN',
          paymentError?.message || error
        );

        if (paymentError?.code === Payment.ERROR_GENERATION_STALE) {
          return;
        }

        if (paymentError?.code === Payment.ERROR_TERMINAL_CANCELLED) {
          state.value = PAYMENT_TERMINAL_STATES.IDLE;
          showTerminalCancelledPopup.value = true;
          return;
        }

        showPaymentError(
          paymentErrorCaptionKey({
            failure: paymentError?.failure,
            timedOut: paymentError?.code === Payment.ERROR_TIMEOUT,
          })
        );
      });
  }

  function trackUserAction(
    message: string,
    level: 'info' | 'warning',
    counter: string,
    source: string,
    clearContext: boolean
  ): void {
    track(
      '[PaymentTerminal] Sentry tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'payment',
          message,
          { attempt_number: paymentAttemptCount.value },
          level
        );
        sentryService.incrementCounter(counter, 1, { source });
        if (clearContext) {
          sentryService.setContext('payment', null);
        }
      },
      warn
    );
  }

  function onRetryClick(): void {
    if (
      isFinalPaymentError.value ||
      state.value === PAYMENT_TERMINAL_STATES.WAITING ||
      payment.isPaymentInProgress
    ) {
      return;
    }

    trackUserAction(
      'User clicked retry after error',
      'info',
      'payment.retry_click',
      'error_screen',
      false
    );

    launch();
  }

  function onCancelClick(): void {
    if (isNavigating.value) {
      return;
    }
    isNavigating.value = true;

    trackUserAction(
      'User cancelled payment from UI',
      'info',
      'payment.user_cancel',
      'cancel_button',
      true
    );

    payment.cancelPayment();
    if (printing.reprintOrder) {
      // The visitor did not pay again for the failed copies: their money
      // stays with the visitor, and the print ends here.
      printing.cancelReprintOrder();
      router.redirectToStep('print-failed');
      return;
    }
    router.redirectToStep('portraits');
  }

  async function endSessionAndReturnHome(): Promise<void> {
    const loadingIndicator = useUiStore().createLoadingIndicator();
    try {
      try {
        await session.end('abandoned');
      } catch (error) {
        warn('[PaymentTerminal] Session end failed:', error);
      }
      if (!isUnmounted.value) {
        await router.redirectToStep('', {
          omitQuery: true,
          localeCode: currentLocale.value?.code,
        });
      }
    } finally {
      loadingIndicator.hide();
    }
  }

  async function exitAfterPaymentError(
    source: PaymentErrorExitSource
  ): Promise<void> {
    clearFinalAutoExit();
    if (isNavigating.value || isUnmounted.value) {
      return;
    }
    isNavigating.value = true;

    trackUserAction(
      source === 'error_screen'
        ? 'User exited session from payment error'
        : 'Session ended without a touch after the final payment error',
      'warning',
      'payment.session_exit',
      source,
      true
    );

    await endSessionAndReturnHome();
  }

  function onExitSessionClick(): Promise<void> {
    return exitAfterPaymentError('error_screen');
  }

  function onPopupContinueClick(): void {
    if (
      state.value === PAYMENT_TERMINAL_STATES.WAITING ||
      payment.isPaymentInProgress
    ) {
      return;
    }
    if (isNavigating.value) {
      return;
    }

    trackUserAction(
      'User chose to continue after terminal cancellation',
      'info',
      'payment.retry_click',
      'terminal_cancelled_popup',
      false
    );

    showTerminalCancelledPopup.value = false;
    launch();
  }

  async function onPopupExitClick(): Promise<void> {
    if (isNavigating.value || isUnmounted.value) {
      return;
    }
    isNavigating.value = true;

    trackUserAction(
      'User exited session after terminal cancellation',
      'warning',
      'payment.session_exit',
      'terminal_cancelled_popup',
      true
    );

    showTerminalCancelledPopup.value = false;
    await endSessionAndReturnHome();
  }

  function enter(exposePublicApi: () => void): void {
    exposePublicApi();
    restApi.abortGenerationRequests();

    track(
      '[PaymentTerminal] Sentry tracking failed:',
      () => {
        sentryService.addBreadcrumb('payment', 'Payment terminal mounted', {
          queue_size: store.printingQueue?.length || 0,
        });
        sentryService.setContext('payment', {
          item_count: store.printingQueue?.length || 0,
          has_amount: Boolean(getTotalSumFormatted()),
          currency: payment.currency || 'unknown',
        });
      },
      warn
    );

    launch();
  }

  function leave(): void {
    isUnmounted.value = true;
    clearFinalAutoExit();

    track(
      '[PaymentTerminal] Sentry cleanup failed:',
      () => {
        sentryService.addBreadcrumb(
          'payment',
          'Payment terminal unmounted',
          {
            state: state.value,
            was_navigating: isNavigating.value,
            attempt_count: paymentAttemptCount.value,
          },
          'info'
        );
        sentryService.setContext('payment', null);
        sentryService.flush(500).catch(() => undefined);
      },
      (label, sentryError) => warn(label, errorMessage(sentryError))
    );

    if (
      state.value !== PAYMENT_TERMINAL_STATES.SUCCESS &&
      !isNavigating.value
    ) {
      payment.cancelPayment();
    }
  }

  const publicApi: PublicApi = proxyRefs({
    _isNavigating: isNavigating,
    _isUnmounted: isUnmounted,
    _paymentAttemptCount: paymentAttemptCount,
    _paymentStartTime: paymentStartTime,
    get isPaymentError(): boolean {
      return isPaymentError.value;
    },
    get isPaymentSuccessful(): boolean {
      return isPaymentSuccessful.value;
    },
    get isWaitingForPayment(): boolean {
      return isWaitingForPayment.value;
    },
    getTotalSumFormatted,
    launch,
    onCancelClick,
    onExitSessionClick,
    onPopupContinueClick,
    onPopupExitClick,
    onRetryClick,
    router,
    showTerminalCancelledPopup,
    state,
    store,
  });

  return {
    enter,
    errorCaptionKey,
    getTotalSumFormatted,
    isFinalPaymentError,
    isNavigating,
    isPaymentError,
    isPaymentSuccessful,
    isUnmounted,
    isWaitingForPayment,
    launch,
    leave,
    onCancelClick,
    onExitSessionClick,
    onPopupContinueClick,
    onPopupExitClick,
    onRetryClick,
    paymentAttemptCount,
    paymentStartTime,
    publicApi,
    showTerminalCancelledPopup,
    state,
  };
}
